/**
 * @file いま扱っているまなぶ人を決め、ヘッダー (LearnerSwitcher) に出す hook。
 *
 * @what: 家族のまなぶ人の候補を読み、端末が覚えている人 (lib/lastLearner.ts) を選ぶ。覚えが
 *   無ければ先頭の人にする。選び直すと端末に覚え直す。選んでいる人と候補はヘッダーに渡し、
 *   ヘッダーからも選び直せる。本を読む面・問題ごとの解説・さくいん・読みものの各面が使う。
 * @why: まなぶ人の名前が本だなの面のヘッダーにしか出ておらず、本を読む面で別の人のまま
 *   学びメモを書いてしまう。どの面でもナビゲーションバーに出しておく (代表の指摘、2026-09-08)。
 *   面ごとに同じ選び方を書いていたので、1つにまとめる。
 * @failure: 候補が読めない・まだ読めていない・家族にまなぶ人がいないときは learnerId が null の
 *   ままで、ヘッダーには何も出ない。null はエラーではなく「まだ決まっていない」状態として扱い、
 *   各面はまなぶ人に依る部分 (解いた結果・ふりかえり・学びメモの新規作成) だけを出さない。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useHeaderLearner } from "@/shared/ui/headerLearnerContext";
import { memberLabel, type FamilyMember } from "../api/members";
import { readLastLearner, writeLastLearner } from "../lib/lastLearner";
import { useLearners } from "./useLearners";

const NO_LEARNERS: FamilyMember[] = [];

export interface LearnerSelection {
  /** 家族のまなぶ人の候補 (読めるまでは空)。 */
  learners: FamilyMember[];
  /** いま選んでいるまなぶ人 (決まるまでは null)。 */
  learnerId: string | null;
  /** 選び直す (端末にも覚える)。 */
  selectLearner: (personId: string) => void;
  /**
   * まなぶ人が決まったか (候補を読み終え、選べる人がいれば選んだ状態) を表す。候補が読めなかった・
   * 家族にまなぶ人がいないときも真にする (learnerId は null のまま)。
   */
  ready: boolean;
}

export function useLearnerSelection(familyId: string): LearnerSelection {
  const learnersState = useLearners(familyId);
  const learners = learnersState.status === "ready" ? learnersState.learners : NO_LEARNERS;
  const [learnerId, setLearnerId] = useState<string | null>(null);

  useEffect(() => {
    if (learnersState.status !== "ready" || learnerId !== null) return;
    const remembered = readLastLearner(familyId);
    const found = learnersState.learners.find((l) => l.person_id === remembered);
    const next = found ?? learnersState.learners[0];
    if (next) setLearnerId(next.person_id);
  }, [learnersState, learnerId, familyId]);

  const selectLearner = useCallback(
    (personId: string) => {
      setLearnerId(personId);
      writeLastLearner(familyId, personId);
    },
    [familyId],
  );

  const options = useMemo(
    () => learners.map((l) => ({ id: l.person_id, label: memberLabel(l) })),
    [learners],
  );
  useHeaderLearner(
    learners.length > 0 ? { options, selectedId: learnerId, onSelect: selectLearner } : null,
  );

  const ready =
    learnersState.status === "error" ||
    (learnersState.status === "ready" && (learnerId !== null || learnersState.learners.length === 0));

  return { learners, learnerId, selectLearner, ready };
}
